import moment from 'moment';
import { Utils } from '../../../../common/js/Utils';
import { AccountCenterListRecordView, ClientAnnuityContractStatus, ClientPolicyStatus } from '../types/Types';
import { RateClasses } from '../../../../common/js/static/RateClasses';
import { NEXTSTATUS } from '../static/AccountCenterStatusMap';

/*
 * Takes a positive integer and outputs a string
 * in the "K", "M", or "B" notation, with one or two decimals
 *
 * Example:
 *    currencyFormatter(300000)
 *   formats to: 300k
 *
 *   currencyFormatter(1000000)
 *   formats to: 1.0M
 *
 *   currencyFormatter(1110000)
 *   formats to: 1.11M
 *
 *   currencyFormatter(1111000)
 *   formats to: 1.111M
 */
export function currencyFormatter(value: number): string {
  if (value === null) {
    return null;
  }
  if (value === 0) {
    return '0';
  }
  if (value >= 1e10) {
    return value / 1e9 + 'B';
  }
  const rmLastChar = (input: string) => {
    return input.slice(0, -1);
  };
  // 10.0 => 10 and 1.000 => 1.0
  const rmLastTwoChar = (input: string) => {
    return input.slice(0, -2);
  };
  const coeffExp = value.toPrecision(4).split('e'),
    base = coeffExp.length === 1 ? 0 : Math.floor(Math.min(parseInt(coeffExp[1].slice(1)), 14) / 3),
    newValue =
      base < 1
        ? value.toFixed(0)
        : (value / Math.pow(10, base * 3))
            .toFixed(3)
            .replace(/\..00/, rmLastTwoChar)
            .replace(/\...0/, rmLastChar)
            .replace(/..\.0/, rmLastTwoChar),
    formatted = newValue + ['', 'K', 'M', 'B'][base];
  return formatted;
}

function statusHasDate(status: ClientPolicyStatus) {
  return status && (!!status.actionRequiredDate || !!status.uwStatusDate);
}

/**
 * This function can be called at the client side to convert a given client policy status' action deadline to the client's local timezone
 */
export function localizeClientPolicyStatus(status: ClientPolicyStatus, isLiteTouch: boolean, tz?: string): void {
  if (statusHasDate(status)) {
    const timeZone = status.timezone || tz;
    const nowInLocal: Date = timeZone
      ? new Date(Utils.now().toLocaleString('en-US', { timeZone: timeZone }))
      : moment(Utils.now()).local().toDate();
    const statusDate = status.uwStatusDate ? status.uwStatusDate : status.actionRequiredDate;
    const actionRequiredDateInLocal: Date = timeZone
      ? new Date(statusDate.toLocaleString('en-US', { timeZone: timeZone }))
      : moment(statusDate).local().toDate();

    const mmdd: string = moment(statusDate).format('M/D');
    const hhmm: string = moment(statusDate).format('h:mma');
    status.subStatus = status.subStatus.replace(/\[MM\/DD\]/, mmdd);
    status.subStatus = status.subStatus.replace(/\[##:##pm\]/, hhmm);
    const labsInLiteTouch =
      (`${status.workflowState}:${status.workflowSubState}` === 'TLIC Issued:Waiting for Labs' ||
        `${status.workflowState}:${status.workflowSubState}` === 'Haven Application:Waiting for Labs') &&
      isLiteTouch
        ? true
        : false;
    if (
      !status.uwStatusDate &&
      !!status.actionRequiredDate &&
      status.importantIfLessThanDays &&
      !labsInLiteTouch &&
      Utils.daysBetweenIgnoreTime(nowInLocal, actionRequiredDateInLocal, true) < Number(status.importantIfLessThanDays)
    ) {
      status.important = true;
    }
  }

  if (status && !status.uwStatusDate && Number(status.importantIfLessThanDays) === -1) {
    status.important = true;
  }
}

export function localizeClientPolicyStatuses(clients: Array<AccountCenterListRecordView>, timeZone?: string) {
  for (const listRecordView of clients) {
    localizeClientPolicyStatus(listRecordView.status, listRecordView.isLiteTouch, timeZone);
  }
}

function isEsignPending(resultRow) {
  return !resultRow.agentEsignComplete && !isUnsignableStage(resultRow);
}

export function isAgentEsignRequired(resultRow): boolean {
  return resultRow.applicationSignedDate && isEsignPending(resultRow);
}

function isUnsignableStage(resultRow) {
  return (
    resultRow.workflowState === 'Advisor Decline Review' ||
    resultRow.workflowState === 'Application Canceled' ||
    resultRow.workflowState === 'Application Canceled By User' ||
    resultRow.workflowState === 'Application Rejected' ||
    resultRow.workflowState === 'Application Declined' ||
    !resultRow.isDocuSignedFormRetrieved
  );
}

export function isAccelerated(resultRow): boolean {
  const tlic = resultRow.tLICRateClass;
  const accelerated = resultRow.acceleratedRateClass;
  // Account for cases where policy is manually moved from Accelerated to TLIC (e.g., upon customer request).
  if (tlic > RateClasses.NO_RATING.code && tlic < RateClasses.KNOCKOUT.code) {
    return false;
  }
  return accelerated > RateClasses.NO_RATING.code && accelerated < RateClasses.BY_PASS_KNOCKOUT.code;
}

export function updateTextForInAppSigning(status: ClientPolicyStatus) {
  const { workflowState, workflowSubState } = status;
  if (workflowState === 'TLIC Application') {
    if (workflowSubState === 'Waiting for Acceptance') {
      status.nextStatus = NEXTSTATUS.clientDecideOnTLICWithInAppSigning;
    } else if (workflowSubState === 'Waiting for Signature') {
      status.nextStatus = NEXTSTATUS.encourageToESignWithInAppSigning;
    }
  } else if (workflowState === 'Haven Application') {
    if (workflowSubState === 'Waiting for Application Acceptance') {
      status.nextStatus = NEXTSTATUS.clientClickESignWithInAppSigning;
    } else if (workflowSubState === 'Waiting for Signature') {
      status.nextStatus = NEXTSTATUS.encourageToESignWithInAppSigning;
    }
  } else if (workflowState === 'Accelerated Application') {
    if (workflowSubState === 'Waiting for Acceptance') {
      status.nextStatus = NEXTSTATUS.clientAcceptanceOfFinalOfferNeeded;
    } else if (workflowSubState === 'Waiting for Signature') {
      status.nextStatus = NEXTSTATUS.encourageToESignWithInAppSigning;
    }
  }
}
